<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  checkFileSizeLimit,
  formatBytes,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// Files on a canned response. Existing files come from the server; new ones
// are uploaded to the blob endpoint on selection and handed back as signed
// ids for the create/update call. The limit is the installation setting.
const props = defineProps({
  existingFiles: { type: Array, default: () => [] },
});
const emit = defineEmits(['update', 'uploading']);

const { t } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');
const maxSizeMb = computed(() =>
  resolveMaximumFileUploadSize(globalConfig.value?.maximumFileUploadSize)
);

const keptFiles = ref([...props.existingFiles]);
const removeIds = ref([]);
const newFiles = ref([]); // { name, size, blobId, fileUrl }
const uploading = ref(false);
const input = ref(null);

const publish = () => {
  emit('update', {
    blobIds: newFiles.value.map(f => f.blobId),
    removeFileIds: removeIds.value,
  });
};

const uploadOne = async file => {
  if (!checkFileSizeLimit(file, maxSizeMb.value)) {
    useAlert(
      t('CANNED_MGMT.ATTACHMENTS.TOO_LARGE', {
        name: file.name,
        size: maxSizeMb.value,
      })
    );
    return;
  }
  try {
    const { blobId, fileUrl } = await uploadFile(file);
    newFiles.value.push({ name: file.name, size: file.size, blobId, fileUrl });
  } catch (e) {
    useAlert(t('CANNED_MGMT.ATTACHMENTS.UPLOAD_ERROR', { name: file.name }));
  }
};

const onSelect = async event => {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;
  uploading.value = true;
  emit('uploading', true);
  try {
    // one after another keeps the order stable and the alerts readable
    await files.reduce(
      (chain, file) => chain.then(() => uploadOne(file)),
      Promise.resolve()
    );
  } finally {
    uploading.value = false;
    emit('uploading', false);
    if (input.value) input.value.value = '';
    publish();
  }
};

const removeExisting = file => {
  keptFiles.value = keptFiles.value.filter(f => f.id !== file.id);
  removeIds.value.push(file.id);
  publish();
};

const removeNew = file => {
  newFiles.value = newFiles.value.filter(f => f.blobId !== file.blobId);
  publish();
};

const openPicker = () => {
  if (input.value) input.value.click();
};
</script>

<template>
  <div class="flex flex-col gap-2 w-full">
    <label>{{ $t('CANNED_MGMT.ATTACHMENTS.LABEL') }}</label>
    <ul
      v-if="keptFiles.length || newFiles.length"
      class="flex flex-col gap-1 m-0 p-0 list-none"
    >
      <li
        v-for="file in keptFiles"
        :key="`kept-${file.id}`"
        class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-n-alpha-1 text-n-slate-12 text-sm"
      >
        <span class="flex items-center gap-2 min-w-0">
          <Icon icon="i-lucide-paperclip" class="size-4 shrink-0" />
          <a
            :href="file.file_url"
            target="_blank"
            rel="noopener noreferrer"
            class="truncate text-n-slate-12"
          >
            {{ file.filename }}
          </a>
          <span class="text-n-slate-11 text-xs shrink-0">
            {{ formatBytes(file.byte_size || 0, 1) }}
          </span>
        </span>
        <Button
          icon="i-lucide-x"
          slate
          ghost
          xs
          :title="$t('CANNED_MGMT.ATTACHMENTS.REMOVE')"
          @click.prevent="removeExisting(file)"
        />
      </li>
      <li
        v-for="file in newFiles"
        :key="`new-${file.blobId}`"
        class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-n-alpha-1 text-n-slate-12 text-sm"
      >
        <span class="flex items-center gap-2 min-w-0">
          <Icon icon="i-lucide-paperclip" class="size-4 shrink-0" />
          <span class="truncate">{{ file.name }}</span>
          <span class="text-n-slate-11 text-xs shrink-0">
            {{ formatBytes(file.size, 1) }}
          </span>
        </span>
        <Button
          icon="i-lucide-x"
          slate
          ghost
          xs
          :title="$t('CANNED_MGMT.ATTACHMENTS.REMOVE')"
          @click.prevent="removeNew(file)"
        />
      </li>
    </ul>
    <div class="flex items-center gap-3">
      <Button
        icon="i-lucide-paperclip"
        slate
        faded
        sm
        type="button"
        :label="$t('CANNED_MGMT.ATTACHMENTS.ADD')"
        :is-loading="uploading"
        :disabled="uploading"
        @click.prevent="openPicker"
      />
      <span class="text-xs text-n-slate-11">
        {{ $t('CANNED_MGMT.ATTACHMENTS.HINT', { size: maxSizeMb }) }}
      </span>
    </div>
    <input ref="input" type="file" multiple class="hidden" @change="onSelect" />
  </div>
</template>
